import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Activity, Loader2, RefreshCw, AlertTriangle, ChevronRight, Layers,
  ArrowUp, ArrowDown, ArrowUpDown, Download, FileText, FileSpreadsheet,
  Save, Check, Fish, Scale, CalendarDays, GripVertical,
} from 'lucide-react';
import { useFeatureVisibility, FEATURE_KEYS } from '@/utils/featureVisibility';
import { ADMIN_MISSING_FISH_FLOOR, ADMIN_BASELINE_LABEL } from '@/utils/adminMissingFishFloor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'live-cage-kpis';

const COLUMNS = [
  { key: 'current_unit',            label: 'Cage',                     type: 'string'     },
  { key: 'fish_dataset',            label: 'Fish Dataset',             type: 'string'     },
  { key: 'current_actual_avg_weight',label: 'Actual Wt (g)',            type: 'number'     },
  { key: 'last_sample_weight',      label: 'Sample Wt (g)',            type: 'number'     },
  { key: 'days_since_last_sample',  label: 'Days Since Sample',        type: 'number'     },
  { key: 'recorded_fish_no',        label: 'Fish Count',               type: 'number'     },
  { key: 'missing_fish',            label: 'Missing Fish',             type: 'analytical' },
  { key: 'rfi_pct',                 label: 'RFI',                      type: 'analytical' },
  { key: 'recent_rfi_7d_pct',       label: 'RFI (7-day avg)',          type: 'number'     },
  { key: 'reported_fcr',            label: 'Reported FCR',             type: 'analytical' },
  { key: 'model_fcr',               label: 'Model FCR',                type: 'analytical' },
  { key: 'samples_performed',       label: 'Samples',                  type: 'number'     },
  { key: 'last_data_date',          label: 'Last Data Date',           type: 'date'       },
];

const NO_SAMPLES_SENTINEL = 'KPI cannot be analysed — at least one sample is required';

const SENTINEL_MAP = {
  'Analysis not run': -Infinity,
  'None detected': 0,
  'N/A': -Infinity,
  'Too small for analysis': -Infinity,
  [NO_SAMPLES_SENTINEL]: -Infinity,
};

const sortKeyOf = (val, type) => {
  if (val == null) return -Infinity;
  if (type === 'analytical') {
    if (typeof val === 'number') return val;
    return SENTINEL_MAP[val] ?? -Infinity;
  }
  if (type === 'number') return typeof val === 'number' ? val : (Number(val) || -Infinity);
  if (type === 'date') return val ? new Date(val).getTime() : -Infinity;
  return String(val ?? '').toLowerCase();
};

/** Colored pill for a sentinel string. Numeric values render as plain
 *  tabular-nums text so real numbers stay unambiguous. */
const Pill = ({ tone, children, title }) => {
  const map = {
    slate:   'bg-slate-100  text-slate-500  dark:bg-slate-800 dark:text-slate-400 ring-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 ring-emerald-200',
    amber:   'bg-amber-50   text-amber-700   dark:bg-amber-900/30 dark:text-amber-300 ring-amber-200',
    rose:    'bg-rose-50    text-rose-700    dark:bg-rose-900/30 dark:text-rose-300 ring-rose-200',
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-full ring-1 ring-inset text-[10px] font-medium tracking-wide ${map[tone] || map.slate}`}
      title={title}
    >
      {children}
    </span>
  );
};

const renderAnalytical = (v, opts = {}) => {
  if (v === 'Analysis not run')          return <Pill tone="slate"   title="No individual analysis has been saved yet">Analysis not run</Pill>;
  if (v === 'None detected')             return <Pill tone="emerald" title="Individual analysis surfaced no missing fish">None detected</Pill>;
  if (v === 'Too small for analysis')    return <Pill tone="amber"   title="Biomass gain / feed evidence was below the FCR computation threshold">Too small</Pill>;
  if (v === NO_SAMPLES_SENTINEL)         return <Pill tone="rose"    title={v}>Needs sample</Pill>;
  if (v === 'N/A')                       return <Pill tone="slate"   title="N/A">N/A</Pill>;
  if (v == null) return <span className="text-slate-400">—</span>;
  if (typeof v === 'number') {
    if (opts.baseline) {
      return (
        <Pill tone="slate" title="Admin baseline floor — the analysis returned zero missing fish, but production cycles almost never end with zero unaccounted fish. This is a conservative floor for admin review, not a genuine detection.">
          {ADMIN_MISSING_FISH_FLOOR.toLocaleString()} <span className="ml-1 opacity-70">(baseline)</span>
        </Pill>
      );
    }
    if (opts.percent) return <span className="tabular-nums font-medium text-slate-800 dark:text-slate-100">{v.toFixed(1)}%</span>;
    if (opts.fcr)     return <span className="tabular-nums font-medium text-slate-800 dark:text-slate-100">{v.toFixed(3)}</span>;
    if (opts.count)   return <span className="tabular-nums font-medium text-slate-800 dark:text-slate-100">{v.toLocaleString()}</span>;
  }
  return <span className="tabular-nums text-slate-700 dark:text-slate-200">{v}</span>;
};

// ── Frozen (always-left) columns ───────────────────────────────────
//   Cage + Fish Dataset stay pinned at positions 0 and 1 no matter
//   how the user reorders the analytical columns. They are also the
//   sticky-left columns that remain visible while the user scrolls
//   horizontally through the rest of the table.
const FROZEN_KEYS = ['current_unit', 'fish_dataset'];
const DEFAULT_COL_WIDTHS = {
  current_unit: 110,
  fish_dataset: 200,
  current_actual_avg_weight: 210,
  last_sample_weight: 175,
  days_since_last_sample: 175,
  recorded_fish_no: 165,
  missing_fish: 150,
  rfi_pct: 95,
  recent_rfi_7d_pct: 165,
  reported_fcr: 150,
  model_fcr: 130,
  samples_performed: 115,
  last_data_date: 130,
};
const COL_ORDER_KEY = 'live-cage-kpis-col-order';

// Per-column cell renderer — used by the dynamic table body so the
// column order can change without duplicating JSX per cell.
const CELL_RENDERERS = {
  current_unit: (r) => (
    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-100">
      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 group-hover:bg-cyan-600" />
      {r.current_unit || '—'}
    </span>
  ),
  fish_dataset: (r) => (
    <div className="flex flex-col">
      <span className="inline-flex items-center gap-1 font-medium truncate max-w-[220px]">
        {r.is_merged_population && <Layers className="inline w-3 h-3 text-violet-600 shrink-0" title="Merged population" />}
        {r.fish_dataset}
      </span>
      {r.previous_units?.length > 0 && (
        <span className="text-[10px] text-slate-400 truncate max-w-[220px]" title={`Previous units: ${r.previous_units.join(' → ')}`}>
          was: {r.previous_units.join(' → ')}
        </span>
      )}
    </div>
  ),
  current_actual_avg_weight: (r) => (r.current_actual_avg_weight != null
    ? <>{r.current_actual_avg_weight}<span className="text-slate-400 ml-0.5">g</span></>
    : <span className="text-slate-400">—</span>),
  last_sample_weight: (r) => (r.last_sample_weight != null
    ? <>{r.last_sample_weight}<span className="text-slate-400 ml-0.5">g</span></>
    : <span className="text-slate-400">—</span>),
  // Days since the last routine sample event. Red "out of date" pill
  // when > 90 days — a common sampling-cadence guard for cage managers.
  // Tooltip surfaces the actual `last_sample_date` so operators can
  // schedule the next sample without opening the detail view.
  days_since_last_sample: (r) => {
    const v = r.days_since_last_sample;
    if (v == null) return <span className="text-slate-400" data-testid="kpi-days-since-sample">—</span>;
    const stale = v > 90;
    const title = r.last_sample_date
      ? `Last sample on ${r.last_sample_date}${stale ? ' — over 90 days ago, out of date' : ''}`
      : '';
    if (stale) {
      return (
        <span
          data-testid="kpi-days-since-sample"
          title={title}
          className="inline-flex items-center px-1.5 py-0.5 rounded-full ring-1 ring-inset text-[10px] font-semibold tabular-nums bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300 ring-rose-200"
        >
          {v.toLocaleString()} d · out of date
        </span>
      );
    }
    return (
      <span
        data-testid="kpi-days-since-sample"
        title={title}
        className="tabular-nums font-medium text-slate-800 dark:text-slate-100"
      >
        {v.toLocaleString()}<span className="text-slate-400 ml-0.5">d</span>
      </span>
    );
  },
  recorded_fish_no: (r) => (r.recorded_fish_no != null
    ? r.recorded_fish_no.toLocaleString()
    : <span className="text-slate-400">—</span>),
  missing_fish: (r) => renderAnalytical(r.missing_fish, { count: true, baseline: !!r.missing_fish_is_baseline }),
  rfi_pct: (r) => renderAnalytical(r.rfi_pct, { percent: true }),
  recent_rfi_7d_pct: (r) => (typeof r.recent_rfi_7d_pct === 'number'
    ? <>{r.recent_rfi_7d_pct.toFixed(1)}<span className="text-slate-400 ml-0.5">%</span></>
    : <span className="text-slate-400">—</span>),
  reported_fcr: (r) => renderAnalytical(r.reported_fcr, { fcr: true }),
  model_fcr: (r) => renderAnalytical(r.model_fcr, { fcr: true }),
  samples_performed: (r) => (r.samples_performed?.toLocaleString() ?? '0'),
  last_data_date: (r) => (r.last_data_date || '—'),
};
const CELL_TESTID_PREFIX = {
  missing_fish: 'kpi-missing',
  rfi_pct: 'kpi-rfi',
  recent_rfi_7d_pct: 'kpi-rfi-7d',
  reported_fcr: 'kpi-fcr-reported',
  model_fcr: 'kpi-fcr-model',
};

const IconBtn = ({ tone = 'cyan', onClick, disabled, icon: Icon, children, ...rest }) => {
  const map = {
    cyan:    'border-cyan-300    text-cyan-700    hover:bg-cyan-50    dark:hover:bg-cyan-900/30',
    emerald: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
    rose:    'border-rose-300    text-rose-700    hover:bg-rose-50    dark:hover:bg-rose-900/30',
    violet:  'border-violet-300  text-violet-700  hover:bg-violet-50  dark:hover:bg-violet-900/30',
    slate:   'border-slate-300   text-slate-600   hover:bg-slate-100  dark:hover:bg-slate-800',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-xs px-2.5 py-1 rounded-md border ${map[tone]} inline-flex items-center gap-1 disabled:opacity-60 transition-colors`}
      {...rest}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </button>
  );
};

export default function LiveCageKPIsSection() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  // Registry-backed visibility. Admin bypasses; normal users only
  // see the section when the feature has been released.
  const { isFeatureVisible, isViaGrant } = useFeatureVisibility();
  const featureVisible = isFeatureVisible(FEATURE_KEYS.LIVE_CAGE_KPIS);
  const showPremiumPreviewBanner = isViaGrant(FEATURE_KEYS.LIVE_CAGE_KPIS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [sortState, setSortState] = useState({ key: 'current_unit', dir: 'asc' });
  const [savedFlash, setSavedFlash] = useState(false);
  const [defaultLoaded, setDefaultLoaded] = useState(false);

  // ── User-adjustable column widths ─────────────────────────────────
  // Widths are persisted in `localStorage` so a drag-to-resize sticks
  // across reloads on the same device. Missing entries fall back to
  // the natural column width driven by content.
  const COL_WIDTHS_KEY = 'live-cage-kpis-col-widths';
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}') || {}; }
    catch { return {}; }
  });
  const startResize = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest('th');
    const startX = e.clientX;
    const startW = colWidths[key] || (th ? th.getBoundingClientRect().width : 120);
    document.body.style.cursor = 'col-resize';
    const onMove = (ev) => {
      const dw = ev.clientX - startX;
      const next = Math.max(60, Math.round(startW + dw));
      setColWidths((prev) => {
        const updated = { ...prev, [key]: next };
        try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(updated)); } catch { /* quota */ }
        return updated;
      });
    };
    const onUp = () => {
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [colWidths]);
  const resetColWidths = useCallback(() => {
    setColWidths({});
    try { localStorage.removeItem(COL_WIDTHS_KEY); } catch { /* ignore */ }
  }, []);
  const hasCustomWidths = Object.keys(colWidths).length > 0;

  // ── User-adjustable column ORDER ─────────────────────────────────
  // Cage + Fish Dataset are always frozen at the left. Everything else
  // is user-reorderable via native drag-and-drop on the header cell;
  // the order persists to localStorage on the same device. Unknown /
  // legacy keys are dropped and any new registered keys are appended
  // to the end so the app never renders a stale ordering.
  const REORDERABLE_KEYS_DEFAULT = COLUMNS
    .map((c) => c.key)
    .filter((k) => !FROZEN_KEYS.includes(k));
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(COL_ORDER_KEY) || 'null');
      if (Array.isArray(raw)) {
        const filtered = raw.filter((k) => REORDERABLE_KEYS_DEFAULT.includes(k));
        const missing = REORDERABLE_KEYS_DEFAULT.filter((k) => !filtered.includes(k));
        return [...filtered, ...missing];
      }
    } catch { /* fall through */ }
    return REORDERABLE_KEYS_DEFAULT;
  });
  const persistColumnOrder = useCallback((next) => {
    setColumnOrder(next);
    try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(next)); } catch { /* quota */ }
  }, []);
  const resetColumnOrder = useCallback(() => {
    setColumnOrder(REORDERABLE_KEYS_DEFAULT);
    try { localStorage.removeItem(COL_ORDER_KEY); } catch { /* ignore */ }
  }, [REORDERABLE_KEYS_DEFAULT]);
  const hasCustomOrder = React.useMemo(
    () => columnOrder.join('|') !== REORDERABLE_KEYS_DEFAULT.join('|'),
    [columnOrder, REORDERABLE_KEYS_DEFAULT]
  );

  // Effective column list: frozen keys first, then user-ordered rest.
  const orderedColumns = React.useMemo(() => {
    const byKey = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
    const frozen = FROZEN_KEYS.map((k) => byKey[k]).filter(Boolean);
    const rest = columnOrder.map((k) => byKey[k]).filter(Boolean);
    return [...frozen, ...rest];
  }, [columnOrder]);

  // Sticky-left offset for the second frozen column (Fish Dataset).
  // Follows the current width of the Cage column so a live resize is
  // reflected without a page reload.
  const stickyLeftForFishDataset =
    (colWidths.current_unit || DEFAULT_COL_WIDTHS.current_unit);

  // Drag-and-drop reorder — native HTML5 for zero-dep implementation.
  const [dragKey, setDragKey] = useState(null);
  const onDragStart = (key) => (e) => {
    setDragKey(key);
    try { e.dataTransfer.setData('text/plain', key); } catch { /* Safari */ }
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onDrop = (targetKey) => (e) => {
    e.preventDefault();
    const src = dragKey || e.dataTransfer.getData('text/plain');
    setDragKey(null);
    if (!src || src === targetKey) return;
    if (FROZEN_KEYS.includes(src) || FROZEN_KEYS.includes(targetKey)) return;
    const next = [...columnOrder];
    const from = next.indexOf(src);
    const to = next.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    next.splice(from, 1);
    next.splice(to, 0, src);
    persistColumnOrder(next);
  };

  // Top scrollbar synchronised with the table's native bottom
  // scrollbar. A skinny sibling div carries a phantom-wide child that
  // matches the table's actual scrollWidth; scroll events flow both
  // ways so either bar drives the other.
  const topScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const syncingRef = React.useRef(false);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  React.useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableScrollWidth(el.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [orderedColumns, rows.length, colWidths]);
  const onTopScroll = () => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    if (tableScrollRef.current && topScrollRef.current) {
      syncingRef.current = true;
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };
  const onTableScroll = () => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    if (topScrollRef.current && tableScrollRef.current) {
      syncingRef.current = true;
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
  };

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/admin/live-cage-kpis`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setRows(r.data?.rows || []); setGeneratedAt(r.data?.generated_at); })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load live KPIs'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Load this user's saved default sort once on mount. When present it
  // wins over the initial `current_unit / asc` fallback so their chosen
  // ordering is preserved across logout/login and browser restarts.
  useEffect(() => {
    if (!token || defaultLoaded) return;
    axios.get(`${API}/preferences/sort-filter/${STORAGE_KEY}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        const pref = r?.data?.preference;
        if (pref?.sortKey && pref?.sortDir) {
          setSortState({ key: pref.sortKey, dir: pref.sortDir });
        }
      })
      .catch(() => { /* 404 = no saved default → keep initial */ })
      .finally(() => setDefaultLoaded(true));
  }, [token, defaultLoaded]);

  const saveSortAsDefault = useCallback(async () => {
    if (!token) return;
    try {
      await axios.put(
        `${API}/preferences/sort-filter/${STORAGE_KEY}`,
        { sortKey: sortState.key, sortDir: sortState.dir, filters: {} },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch { /* silent */ }
  }, [token, sortState]);

  const sortedRows = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortState.key);
    if (!col) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const ka = sortKeyOf(a[col.key], col.type);
      const kb = sortKeyOf(b[col.key], col.type);
      if (ka < kb) return sortState.dir === 'asc' ? -1 : 1;
      if (ka > kb) return sortState.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortState]);

  const toggleSort = (key) => {
    setSortState((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  // ── Summary chips (visual only, cheap to compute from rows).
  const summary = useMemo(() => {
    const totalCages = rows.length;
    const analysed = rows.filter((r) => r.analysis_ran).length;
    const speciesMix = Array.from(new Set(rows.map((r) => r.species).filter(Boolean)));
    const totalFish = rows.reduce((s, r) => s + (Number(r.recorded_fish_no) || 0), 0);
    return { totalCages, analysed, speciesMix, totalFish };
  }, [rows]);

  const exportCsv = () => {
    const headerCells = COLUMNS.map((c) => c.label).join(',');
    const csvValue = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const bodyLines = sortedRows.map((r) => COLUMNS.map((c) => csvValue(cellForExport(r[c.key], c, r))).join(','));
    const blob = new Blob([[headerCells, ...bodyLines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.download = `live-cage-kpis-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const cellForExport = (v, col, r) => {
    if (v == null) return '';
    if (typeof v === 'number') {
      if (col.key === 'missing_fish' && r?.missing_fish_is_baseline) {
        return `${v} (baseline)`;
      }
      if (col.key === 'rfi_pct') return `${v.toFixed(1)}%`;
      if (col.key === 'recent_rfi_7d_pct') return `${v.toFixed(1)}%`;
      if (col.key === 'reported_fcr' || col.key === 'model_fcr') return v.toFixed(3);
      return v;
    }
    return v;
  };

  const exportXlsx = () => {
    const header = COLUMNS.map((c) => c.label);
    const body = sortedRows.map((r) => COLUMNS.map((c) => cellForExport(r[c.key], c, r)));
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    ws['!cols'] = COLUMNS.map((c, i) => ({ wch: i < 2 ? 22 : 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Live Cage KPIs');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    XLSX.writeFile(wb, `live-cage-kpis-${stamp}.xlsx`);
  };

  const exportPdf = () => {
    const pdf = new jsPDF('l', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    pdf.setFillColor(8, 145, 178);
    pdf.rect(0, 0, pageWidth, 16, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(13); pdf.setFont(undefined, 'bold');
    pdf.text('Live Cage KPIs', margin, 10);
    pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
    const stamp = generatedAt ? new Date(generatedAt).toLocaleString() : new Date().toLocaleString();
    pdf.text(`Generated: ${stamp}`, pageWidth - margin, 10, { align: 'right' });
    let y = 22;
    pdf.setTextColor(30, 41, 59);
    const usableWidth = pageWidth - margin * 2;
    const widths = COLUMNS.map((c) =>
      c.key === 'fish_dataset' ? usableWidth * 0.14 :
      c.key === 'current_unit' ? usableWidth * 0.08 :
      (usableWidth * 0.78) / (COLUMNS.length - 2)
    );
    pdf.setFillColor(226, 232, 240);
    pdf.rect(margin, y - 4, usableWidth, 6, 'F');
    pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    let x = margin;
    COLUMNS.forEach((c, i) => {
      const label = c.label.replace(/ \(g\)$/, '');
      pdf.text(label, x + 1, y);
      x += widths[i];
    });
    y += 4;
    pdf.setFont(undefined, 'normal');
    sortedRows.forEach((r) => {
      if (y > pageHeight - margin - 6) { pdf.addPage(); y = margin + 6; }
      x = margin;
      COLUMNS.forEach((c, i) => {
        const v = cellForExport(r[c.key], c);
        let text = v == null ? '—' : String(v);
        if (text.length > 22) text = text.slice(0, 21) + '…';
        pdf.text(text, x + 1, y);
        x += widths[i];
      });
      y += 5;
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, y - 3, pageWidth - margin, y - 3);
    });
    pdf.setFontSize(6); pdf.setTextColor(148, 163, 184);
    pdf.text(
      'Missing Fish / RFI / Reported FCR / Model FCR come verbatim from the saved individual Spreadsheet Uploader analysis — never recomputed here.',
      margin, pageHeight - 4,
    );
    const stamp2 = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    pdf.save(`live-cage-kpis-${stamp2}.pdf`);
  };

  // Admin always sees the panel. Non-admins see it only when the
  // "Live Cage KPIs" feature has been released via the Admin-Only
  // Feature Visibility panel. Backend enforces the same gate on
  // GET /api/admin/live-cage-kpis — this is a UX guard, not a
  // security boundary.
  if (!user?.is_admin && !featureVisible) return null;

  const freshness = generatedAt ? new Date(generatedAt).toLocaleString() : null;

  return (
    <Card
      className="border-0 shadow-lg overflow-hidden ring-1 ring-cyan-100 dark:ring-slate-800 bg-white dark:bg-slate-900"
      data-testid="live-cage-kpis-section"
    >
      {/* Gradient header band */}
      <CardHeader className="bg-gradient-to-r from-cyan-600 via-cyan-500 to-teal-500 dark:from-cyan-800 dark:via-cyan-900 dark:to-teal-900 text-white px-5 py-4 flex flex-row items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-white">
            <div className="p-1.5 rounded-lg bg-white/20 backdrop-blur-sm">
              <Activity className="w-5 h-5" />
            </div>
            <span className="text-lg font-semibold tracking-tight">Live Cage KPIs</span>
            {showPremiumPreviewBanner ? (
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-sm"
                data-testid="live-cage-kpis-premium-badge"
                title="Premium feature — available for preview on your account"
              >
                Premium Preview
              </span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-white text-cyan-700">Admin</span>
            )}
          </CardTitle>
          <p className="text-[12px] text-cyan-50/90 mt-1 max-w-2xl leading-snug">
            Read-only summary of currently active FishTalk populations. Click any row to open the full individual analysis.
            {freshness && <span className="ml-1 opacity-80">Updated {freshness}.</span>}
          </p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            type="button" onClick={exportCsv} disabled={loading || !rows.length}
            className="text-xs px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
            data-testid="live-cage-kpis-export"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
          <button
            type="button" onClick={exportXlsx} disabled={loading || !rows.length}
            className="text-xs px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
            data-testid="live-cage-kpis-export-xlsx"
          >
            <FileSpreadsheet className="w-3 h-3" /> Excel
          </button>
          <button
            type="button" onClick={exportPdf} disabled={loading || !rows.length}
            className="text-xs px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
            data-testid="live-cage-kpis-export-pdf"
          >
            <FileText className="w-3 h-3" /> PDF
          </button>
          <button
            type="button" onClick={load} disabled={loading}
            className="text-xs px-2.5 py-1 rounded-md bg-white text-cyan-700 hover:bg-cyan-50 inline-flex items-center gap-1 disabled:opacity-50 transition-colors font-medium"
            data-testid="live-cage-kpis-refresh"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </CardHeader>

      {/* Premium Feature Preview banner — rendered only when the user
          sees this feature via an individual per-user grant (not a
          site-wide release and not admin). Makes it explicit that
          this is a paid feature being previewed on their account. */}
      {showPremiumPreviewBanner && (
        <div
          className="flex items-start gap-3 px-5 py-3 border-y border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30"
          data-testid="live-cage-kpis-premium-banner"
        >
          <div className="p-1.5 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 text-white flex-shrink-0 shadow-sm">
            <Activity className="w-4 h-4" />
          </div>
          <div className="text-[13px] leading-snug text-amber-900 dark:text-amber-200">
            <p className="font-semibold">Premium Feature Presentation</p>
            <p className="text-[12px] text-amber-800/90 dark:text-amber-300/90 mt-0.5">
              This dashboard is a premium feature made available on your account as a preview.
              Contact PFD support to keep it active after the presentation period.
            </p>
          </div>
        </div>
      )}

      {/* Summary strip */}
      {rows.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-5 pt-4 pb-1" data-testid="live-cage-kpis-summary">
          <SummaryTile icon={Fish}          label="Active cages"   value={summary.totalCages.toLocaleString()} tone="cyan" />
          <SummaryTile icon={Activity}      label="With analysis"  value={`${summary.analysed} / ${summary.totalCages}`} tone="emerald" />
          <SummaryTile icon={Scale}         label="Total fish"     value={summary.totalFish.toLocaleString()} tone="violet" />
          <SummaryTile icon={CalendarDays}  label="Species"        value={summary.speciesMix.join(', ') || '—'} tone="slate" />
        </div>
      )}

      <CardContent className="pt-3">
        {error && (
          <div className="p-3 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {String(error)}
          </div>
        )}
        {!error && !loading && rows.length === 0 && (
          <div className="text-sm text-slate-500 italic py-8 text-center" data-testid="live-cage-kpis-empty">
            No active populations yet. Upload a FishTalk or AquaManager export to see live KPIs here.
          </div>
        )}

        {rows.length > 0 && (
          <>
            {/* Sort-preferences bar */}
            <div className="flex items-center gap-2 flex-wrap px-1 py-2 mb-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                Sort
              </span>
              <span className="text-xs text-slate-700 dark:text-slate-200 font-medium inline-flex items-center gap-1">
                {COLUMNS.find((c) => c.key === sortState.key)?.label || '—'}
                {sortState.dir === 'asc' ? <ArrowUp className="w-3 h-3 text-cyan-600" /> : <ArrowDown className="w-3 h-3 text-cyan-600" />}
              </span>
              <button
                type="button"
                onClick={saveSortAsDefault}
                className={`ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md transition-colors ${
                  savedFlash
                    ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30'
                    : 'text-cyan-700 hover:bg-cyan-50 dark:hover:bg-cyan-900/30'
                }`}
                data-testid="live-cage-kpis-save-sort"
                title="Remember this sort order for the next time you visit"
              >
                {savedFlash ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
                {savedFlash ? 'Saved as default' : 'Save as default'}
              </button>
              <span className="ml-auto text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
                {hasCustomOrder && (
                  <button
                    type="button"
                    onClick={resetColumnOrder}
                    className="text-cyan-700 hover:underline"
                    data-testid="live-cage-kpis-reset-col-order"
                    title="Restore the default column order"
                  >
                    Reset order
                  </button>
                )}
                {hasCustomWidths && (
                  <button
                    type="button"
                    onClick={resetColWidths}
                    className="text-cyan-700 hover:underline"
                    data-testid="live-cage-kpis-reset-col-widths"
                    title="Restore all columns to their default widths"
                  >
                    Reset widths
                  </button>
                )}
                {rows.length} {rows.length === 1 ? 'cage' : 'cages'}
              </span>
            </div>

            {/* Top horizontal scrollbar — mirrors the table's native
                bottom scrollbar so the operator can scan wide grids
                without dropping to the last row first. Kept 12 px tall
                to remain a clear, tactile scroll target. */}
            <div
              ref={topScrollRef}
              onScroll={onTopScroll}
              className="overflow-x-auto overflow-y-hidden rounded-t-lg ring-1 ring-b-0 ring-slate-100 dark:ring-slate-800"
              data-testid="live-cage-kpis-top-scroll"
              style={{ height: 12 }}
            >
              <div style={{ width: tableScrollWidth || '100%', height: 1 }} />
            </div>

            <div
              ref={tableScrollRef}
              onScroll={onTableScroll}
              className="overflow-x-auto rounded-b-lg ring-1 ring-t-0 ring-slate-100 dark:ring-slate-800"
            >
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="live-cage-kpis-table">
                <thead className="bg-slate-50 dark:bg-slate-800/60 sticky top-0 z-10">
                  <tr className="text-left text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {orderedColumns.map((c, idx) => {
                      const active = sortState.key === c.key;
                      const isFrozen = FROZEN_KEYS.includes(c.key);
                      const width = colWidths[c.key] || DEFAULT_COL_WIDTHS[c.key];
                      const stickyStyle = isFrozen
                        ? {
                            position: 'sticky',
                            left: c.key === 'current_unit' ? 0 : stickyLeftForFishDataset,
                            zIndex: 20,
                          }
                        : undefined;
                      const style = {
                        ...(width ? { width: `${width}px`, minWidth: `${width}px` } : {}),
                        ...(stickyStyle || {}),
                      };
                      return (
                        <th
                          key={c.key}
                          onClick={() => toggleSort(c.key)}
                          draggable={!isFrozen}
                          onDragStart={!isFrozen ? onDragStart(c.key) : undefined}
                          onDragOver={!isFrozen ? onDragOver : undefined}
                          onDrop={!isFrozen ? onDrop(c.key) : undefined}
                          style={style}
                          className={`relative px-3 py-2.5 select-none overflow-hidden transition-colors hover:text-cyan-700 dark:hover:text-cyan-300 ${
                            isFrozen ? 'bg-slate-50 dark:bg-slate-800/95 cursor-pointer' : 'cursor-move'
                          } ${c.type !== 'string' ? 'text-right' : ''} ${
                            active ? 'text-cyan-700 dark:text-cyan-300 font-semibold' : ''
                          } ${dragKey === c.key ? 'opacity-50' : ''}`}
                          data-testid={`sort-${c.key}`}
                          title={isFrozen ? c.label : `${c.label} — drag to reorder · click to sort`}
                        >
                          <span className={`flex items-center gap-1 min-w-0 ${c.type !== 'string' ? 'flex-row-reverse justify-start' : ''}`}>
                            {!isFrozen && <GripVertical className="w-3 h-3 text-slate-300 dark:text-slate-600 shrink-0" aria-hidden />}
                            <span className="truncate">{c.label}</span>
                            {active
                              ? (sortState.dir === 'asc' ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)
                              : <ArrowUpDown className="w-3 h-3 text-slate-300 dark:text-slate-600 shrink-0" />
                            }
                          </span>
                          <span
                            onPointerDown={(e) => startResize(e, c.key)}
                            onClick={(e) => e.stopPropagation()}
                            role="separator"
                            aria-orientation="vertical"
                            title="Drag edge to resize"
                            data-testid={`col-resizer-${c.key}`}
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-cyan-400/70 active:bg-cyan-500"
                          />
                        </th>
                      );
                    })}
                    <th className="px-2 py-2" style={{ width: 24 }} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r, idx) => {
                    const zebra = idx % 2 === 1;
                    const rowBg = zebra ? 'bg-slate-50/40 dark:bg-slate-900/40' : 'bg-white dark:bg-slate-900';
                    // Sticky cells need a SOLID background — semi-transparent
                    // zebra shades leak the underlying scrolled content. Use
                    // hard slate-50 / white to match visual banding.
                    const stickyBg = zebra ? 'bg-slate-100 dark:bg-slate-800' : 'bg-white dark:bg-slate-900';
                    return (
                      <tr
                        key={r.record_id}
                        className={`border-b border-slate-100 dark:border-slate-800 cursor-pointer group transition-colors ${rowBg} hover:bg-cyan-50/70 dark:hover:bg-slate-800`}
                        onClick={() => navigate(`/spreadsheet-comparison?recordId=${r.record_id}`)}
                        data-testid={`live-cage-kpi-row-${r.record_id}`}
                      >
                        {orderedColumns.map((c) => {
                          const isFrozen = FROZEN_KEYS.includes(c.key);
                          const renderer = CELL_RENDERERS[c.key];
                          const stickyStyle = isFrozen
                            ? { position: 'sticky', left: c.key === 'current_unit' ? 0 : stickyLeftForFishDataset, zIndex: 5 }
                            : undefined;
                          const align = c.type === 'string' ? '' : 'text-right';
                          const testId = CELL_TESTID_PREFIX[c.key] ? `${CELL_TESTID_PREFIX[c.key]}-${r.record_id}` : undefined;
                          return (
                            <td
                              key={c.key}
                              style={stickyStyle}
                              className={`px-3 py-2.5 ${align} ${isFrozen ? `${stickyBg} font-medium` : ''} ${c.type === 'number' ? 'tabular-nums text-slate-800 dark:text-slate-100' : ''} ${c.type === 'date' ? 'text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap' : ''}`}
                              data-testid={testId}
                            >
                              {renderer ? renderer(r) : (r[c.key] ?? <span className="text-slate-400">—</span>)}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2.5 text-slate-300 group-hover:text-cyan-600 transition-colors">
                          <ChevronRight className="w-4 h-4" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-3 leading-relaxed">
              Missing Fish / RFI / Reported FCR / Model FCR come verbatim from the saved individual Spreadsheet Uploader analysis — never recomputed here.
              Populations without a valid current Closing Count are hidden. Superseded / merged predecessors do not appear as active cages.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const SummaryTile = ({ icon: Icon, label, value, tone = 'cyan' }) => {
  const map = {
    cyan:    'from-cyan-50 to-white ring-cyan-100 text-cyan-700 dark:from-cyan-950/50 dark:to-slate-900 dark:ring-cyan-900 dark:text-cyan-300',
    emerald: 'from-emerald-50 to-white ring-emerald-100 text-emerald-700 dark:from-emerald-950/50 dark:to-slate-900 dark:ring-emerald-900 dark:text-emerald-300',
    violet:  'from-violet-50 to-white ring-violet-100 text-violet-700 dark:from-violet-950/50 dark:to-slate-900 dark:ring-violet-900 dark:text-violet-300',
    slate:   'from-slate-50 to-white ring-slate-200 text-slate-700 dark:from-slate-800/50 dark:to-slate-900 dark:ring-slate-700 dark:text-slate-300',
  };
  return (
    <div className={`bg-gradient-to-br ${map[tone]} ring-1 rounded-lg px-3 py-2 flex items-center gap-3`}>
      <div className="p-1.5 rounded-md bg-white/70 dark:bg-slate-800/60 shadow-sm">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">{label}</div>
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate" title={String(value)}>
          {value}
        </div>
      </div>
    </div>
  );
};
